/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC, useMemo, useState } from "react";
import { BEM } from "@adapptio/lib-components";
import { createEmptyScope, Type } from "@adapptio/lib-blueprint";

import { Panel, PANEL_STYLE, PanelNote } from "../../../../ui/components/Panel";
import { EndpointEditorEditView } from "../EndpointEditorEditView";
import { ILayoutConfig, LAYOUT_TYPE } from "../../../../ui/components/Layout/LayoutTypes";
import { Layout } from "../../../../ui/components/Layout/Layout";
import { LayoutState } from "../../../../ui/components/Layout/LayoutState";
import { useSimpleEvent } from "../../../../ui/hooks";
import { ScopeViewer } from "../../../../ui/components/ScopeViewer/ScopeViewer";
// import { offEvent, onEvent } from "@adapptio/lib-shared";
import { Inspector } from "../../../../inspector";
import { NODE_RESULT_TYPE } from "@adapptio/lib-core";

export const EndpointEditorTesterLayout: ILayoutConfig = {
	root: {
		type: LAYOUT_TYPE.ROW,
		id: "layout",
		children: [
			{
				type: LAYOUT_TYPE.COLUMN,
				id: "panelLeft",
				size: "300px",
				isExpandable: true,
				defaultExpanded: true,
				children: [
					{
						type: LAYOUT_TYPE.PANEL,
						id: "parameters",
						isExpandable: false,
						contentComponent: "parameters"
					}
				]
			},
			{
				type: LAYOUT_TYPE.SPLITTER,
				id: "leftSplitter"
			},
			{
				type: LAYOUT_TYPE.COLUMN,
				id: "main",
				children: [
					{
						type: LAYOUT_TYPE.PANEL,
						id: "output",
						isExpandable: false,
						contentComponent: "output"
					}
				]
			}
		]
	}
};

export interface IEndpointEditorTesterContentProps {
	view: EndpointEditorEditView;
}

export const EndpointEditorTesterContent: FC<IEndpointEditorTesterContentProps> = ({ view }) => {
	const [ layoutState ] = useState(() => new LayoutState(EndpointEditorTesterLayout));
	const [ executionResult, setExecutionResult ] = useState(view.getExecutionResult());
	const [ paramsModel, setParamsModel ] = useState(view.getEndpointModel().getParamsModel());

	const executionResultScope = useMemo(() => {
		if (executionResult?.status === NODE_RESULT_TYPE.SUCCESS) {
			return createEmptyScope(
				{
					status: executionResult?.status,
					output: executionResult?.data
				},
				{
					status: Type.String({
						label: "Status"
					}),
					output: executionResult?.type
				}
			);
		} else if (executionResult?.status === NODE_RESULT_TYPE.ERROR) {
			return createEmptyScope(
				{
					status: executionResult?.status,
					error: executionResult?.lastError
				},
				{
					status: Type.String({
						label: "Status"
					}),
					error: Type.Any({
						label: "Error"
					})
				}
			);
		}

		return createEmptyScope();
	}, [ executionResult ]);

	useSimpleEvent(view.getEndpointModel().onParamsModelBuilt, () => {
		setParamsModel(view.getEndpointModel().getParamsModel());
	});

	useSimpleEvent(view.onExecuted, () => setExecutionResult(view.getExecutionResult()), [ view ], true);

	const renderComponent = (componentName: string) => {
		switch (componentName) {
			case "parameters":
				return (
					<Panel style={PANEL_STYLE.PRIMARY} scroll={true}>
						<Inspector model={paramsModel} />
					</Panel>
				);
			case "output":
				return (
					<Panel style={PANEL_STYLE.BASE} scroll={true}>
						{executionResult ? (
							<>
								<ScopeViewer scope={executionResultScope} defaultLevelsExpanded={1} />
							</>
						) : (
							<PanelNote>Run endpoint to display it's output.</PanelNote>
						)}
					</Panel>
				);
			default:
				return <p>Error: Undefined panel {componentName}.</p>;
		}
	};

	const baseClass = BEM("edt-module-endpoint-editor-tester");

	return (
		<div className={baseClass.element("content").toClassName()}>
			<Layout state={layoutState} renderComponent={renderComponent} />
		</div>
	);
};
